import { unsafeCSS, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { Options } from 'highcharts';
import { BeaconChartBase } from '@trp-beacon-ui-kit/foundation/components';

import { PopBaseComponent } from '../../base/pop-base-component.js';
import {
  CompositePerformanceCategories,
  Constants,
  DISCLAIMER_CODES,
  CurrentYearPerformanceCategories,
  PerformanceTypes,
} from './total-returns-chart.constants.js';
import {
  TotalReturnsChart,
  TotalReturnsChartDataType,
  TotalReturnsChartType,
} from './total-returns-chart.model.js';
import { dateShort } from '../../common/utils/formatDate.js';
import { restrictLastLegendItemClick } from '../../common/utils/sharedChartOptions.js';
import {
  hasAnnualizedPerformanceDataForPeriod,
  AnnualizedPerformancePeriod,
  hasCurrentYearPerformanceDataForPeriod,
  CurrentYearPerformancePeriod,
} from '../total-returns/total-returns.utils.js';
import { AssetClassEnum } from '../../common/enums/asset-class.js';
import style from './total-returns-chart.scss?inline';

/**
 * @tag pop-total-returns-chart-component
 * @attribute {TotalReturnsChart} [data-component-object] Total returns chart content
 *
 * @rally `BeaconChartBase.tooltipPointHTML` cannot be called without adding in the entirety
 *  of `BeaconChartBase`, which then injects lots of DOM apis into the final build
 * fix: https://rally1.rallydev.com/#/?detail=/userstory/798220893939
 */
@customElement(Constants.TAG_NAME)
export class TotalReturnsChartComponent extends PopBaseComponent {
  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * The component object that contains the pre-fetched data to be rendered.
   */
  componentObject?: TotalReturnsChart;

  firstUpdated(): void {
    const chart = this.shadowRoot?.querySelector('beacon-bar-chart');
    if (chart) {
      // This is necessary because the pointFormatter option for the tooltip is a function and doesn't
      // work correctly when only passed in the options attribute to the beacon-bar-chart element.
      chart.options = this.getOptions();
    }
  }

  /*
   * Component Methods
   */
  get inceptionDate() {
    return this.componentObject?.portfolioInceptionDate
      ? dateShort(this.componentObject?.portfolioInceptionDate)
      : '';
  }

  // Mapping object for current year performance categories
  get currentYearPerformancePeriodToCategory() {
    return {
      [CurrentYearPerformancePeriod.YearToDate]:
        CurrentYearPerformanceCategories.MonthlyYTD,
      [CurrentYearPerformancePeriod.OneMonth]:
        CurrentYearPerformanceCategories.OneMonth,
      [CurrentYearPerformancePeriod.ThreeMonths]:
        CurrentYearPerformanceCategories.ThreeMonths,
    };
  }

  // Helper function to extract current year performance data
  extractCurrentYearPerformanceData(values, type) {
    return [
      ...(hasCurrentYearPerformanceDataForPeriod(
        values,
        CurrentYearPerformancePeriod.YearToDate,
      )
        ? [values[type]?.yearToDate]
        : []),
      ...(hasCurrentYearPerformanceDataForPeriod(
        values,
        CurrentYearPerformancePeriod.OneMonth,
      )
        ? [values[type]?.oneMonth]
        : []),
      ...(hasCurrentYearPerformanceDataForPeriod(
        values,
        CurrentYearPerformancePeriod.ThreeMonths,
      )
        ? [values[type]?.threeMonth]
        : []),
    ];
  }

  // Mapping object for annualized performance categories
  get annualizedPerformancePeriodToCategory() {
    return {
      [AnnualizedPerformancePeriod.OneYear]:
        CompositePerformanceCategories.OneYear,
      [AnnualizedPerformancePeriod.ThreeYear]:
        CompositePerformanceCategories.ThreeYear,
      [AnnualizedPerformancePeriod.FiveYear]:
        CompositePerformanceCategories.FiveYear,
      [AnnualizedPerformancePeriod.TenYear]:
        CompositePerformanceCategories.TenYear,
      [AnnualizedPerformancePeriod.SinceInception]: `${CompositePerformanceCategories.SinceInception}<br>${this.inceptionDate}`,
    };
  }

  // Helper function to extract annualized data
  extractAnnualizedData(values, type) {
    return [
      ...(hasAnnualizedPerformanceDataForPeriod(
        values,
        AnnualizedPerformancePeriod.OneYear,
      )
        ? [values[type]?.oneYear]
        : []),
      ...(hasAnnualizedPerformanceDataForPeriod(
        values,
        AnnualizedPerformancePeriod.ThreeYear,
      )
        ? [values[type]?.threeYear]
        : []),
      ...(hasAnnualizedPerformanceDataForPeriod(
        values,
        AnnualizedPerformancePeriod.FiveYear,
      )
        ? [values[type]?.fiveYear]
        : []),
      ...(hasAnnualizedPerformanceDataForPeriod(
        values,
        AnnualizedPerformancePeriod.TenYear,
      )
        ? [values[type]?.tenYear]
        : [values[type]?.sinceInception]),
    ];
  }

  getCategories() {
    const isCurrentYearPerformance =
      this.componentObject?.chartType ===
      TotalReturnsChartType.CurrentYearPerformance;

    const isMonthlyDataType =
      this.componentObject?.dataValueType === TotalReturnsChartDataType.Monthly;
    let values;

    if (isCurrentYearPerformance) {
      values = this.componentObject?.currentYearPerformanceValues?.monthEnd;
    } else if (isMonthlyDataType) {
      values = this.componentObject?.annualizedPerformanceValues?.monthEnd;
    } else {
      values = this.componentObject?.annualizedPerformanceValues?.quarterEnd;
    }

    const hasDataForPeriod = isCurrentYearPerformance
      ? hasCurrentYearPerformanceDataForPeriod
      : hasAnnualizedPerformanceDataForPeriod;

    const periodToCategory = isCurrentYearPerformance
      ? this.currentYearPerformancePeriodToCategory
      : this.annualizedPerformancePeriodToCategory;

    return Object.keys(periodToCategory)
      .filter((period) => hasDataForPeriod(values, period))
      .map((period) => periodToCategory[period])
      .join(',');
  }

  getCurrentYearPerformanceSeries() {
    const currentYearValues =
      this.componentObject?.currentYearPerformanceValues?.monthEnd;
    const assetClass = this.componentObject?.assetClass;

    const indicatorSymbol =
      assetClass === AssetClassEnum.FIXED_INCOME
        ? this.footnoteService?.getIndicatorSymbol(
            PerformanceTypes.TOTAL_RETURNS_FIXED_INCOME_DISCLAIMER_CODE,
          )
        : this.footnoteService?.getIndicatorSymbol(
            PerformanceTypes.TOTAL_RETURNS_DISCLAIMER_CODE,
          );

    return [
      {
        name:
          assetClass === AssetClassEnum.FIXED_INCOME
            ? PerformanceTypes.PureGrossFixedIncome + indicatorSymbol
            : PerformanceTypes.PureGross + indicatorSymbol,
        data: this.extractCurrentYearPerformanceData(
          currentYearValues,
          'pureGross',
        ),
      },
      {
        name:
          assetClass === AssetClassEnum.FIXED_INCOME
            ? PerformanceTypes.NetFixedIncome + indicatorSymbol
            : PerformanceTypes.Net + indicatorSymbol,
        data: this.extractCurrentYearPerformanceData(currentYearValues, 'net'),
      },
      {
        name: PerformanceTypes.Benchmark,
        data: this.extractCurrentYearPerformanceData(
          currentYearValues,
          'benchmark',
        ),
      },
    ];
  }

  getAnnualizedPerformanceSeries() {
    const annualizedValues =
      this.componentObject?.dataValueType === TotalReturnsChartDataType.Monthly
        ? this.componentObject?.annualizedPerformanceValues?.monthEnd
        : this.componentObject?.annualizedPerformanceValues?.quarterEnd;
    const assetClass = this.componentObject?.assetClass;

    const indicatorSymbol =
      assetClass === AssetClassEnum.FIXED_INCOME
        ? this.footnoteService?.getIndicatorSymbol(
            PerformanceTypes.TOTAL_RETURNS_FIXED_INCOME_DISCLAIMER_CODE,
          )
        : this.footnoteService?.getIndicatorSymbol(
            PerformanceTypes.TOTAL_RETURNS_DISCLAIMER_CODE,
          );

    return [
      {
        name:
          assetClass === AssetClassEnum.FIXED_INCOME
            ? PerformanceTypes.PureGrossFixedIncome + indicatorSymbol
            : PerformanceTypes.PureGross + indicatorSymbol,
        data: this.extractAnnualizedData(annualizedValues, 'pureGross'),
      },
      {
        name:
          assetClass === AssetClassEnum.FIXED_INCOME
            ? PerformanceTypes.NetFixedIncome + indicatorSymbol
            : PerformanceTypes.Net + indicatorSymbol,
        data: this.extractAnnualizedData(annualizedValues, 'net'),
      },
      {
        name: PerformanceTypes.Benchmark,
        data: this.extractAnnualizedData(annualizedValues, 'benchmark'),
      },
    ];
  }

  getSeriesData() {
    if (
      this.componentObject?.chartType ===
      TotalReturnsChartType.CurrentYearPerformance
    ) {
      return this.getCurrentYearPerformanceSeries();
    } else {
      return this.getAnnualizedPerformanceSeries();
    }
  }

  getOptions(): Options {
    return {
      yAxis: {
        labels: {
          format: '{text}%',
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        headerFormat: BeaconChartBase.tooltipHeaderHTML('{point.key}'),
        pointFormatter() {
          return BeaconChartBase.tooltipPointHTML(
            this,
            this.series?.name,
            `${this.y}%`,
          );
        },
      },
      legend: restrictLastLegendItemClick(),
      exporting: {
        enabled: false,
      },
      responsive: {
        rules: [
          {
            condition: { maxWidth: 609 },
            chartOptions: {
              plotOptions: {
                column: {
                  groupPadding: 0.2,
                  pointPadding: 0.15,
                  maxPointWidth: 10,
                },
              },
            },
          },
          {
            condition: { minWidth: 610, maxWidth: 959 },
            chartOptions: {
              plotOptions: {
                column: {
                  groupPadding: 0.2,
                  pointPadding: 0.15,
                  maxPointWidth: 30,
                },
              },
            },
          },
          {
            condition: { minWidth: 960, maxWidth: 1279 },
            chartOptions: {
              plotOptions: {
                column: {
                  groupPadding: 0.15,
                  pointPadding: 0.08,
                  maxPointWidth: 40,
                },
              },
            },
          },
          {
            condition: { minWidth: 1280 },
            chartOptions: {
              plotOptions: {
                column: {
                  groupPadding: 0.1,
                  pointPadding: 0.16,
                },
              },
            },
          },
        ],
      },
    };
  }

  /**
   * Temp fix for US637567
   * Highchart strips out disclaimer code attributes due to XSS prevention
   * Which prevent us from rendering the disclaimer code indication
   * Raised a feature request in the Foundation to allow these attributes
   * Remove this span once Foundation team allow customization of allowedAttributes in Highchart
   */
  private __renderHiddenDisclaimerCodes() {
    // we only have Net and Pure Gross in classifications
    const assetClass = this.componentObject?.assetClass;
    const disclaimerCode =
      assetClass === AssetClassEnum.FIXED_INCOME
        ? DISCLAIMER_CODES['TOTAL_RETURNS_FIXED_INCOME_DISCLAIMER_CODE']
        : DISCLAIMER_CODES['TOTAL_RETURNS_DISCLAIMER_CODE'];

    return html`<span
      class="total-returns-chart__disclaimer"
      data-disclaimer-code="${disclaimerCode}"
    ></span>`;
  }

  render() {
    if (!this.componentObject) {
      return html``;
    }
    return this.renderComponent(
      html`<beacon-bar-chart
          variant="vertical"
          tooltip-variant="multi-level"
          visible-legend-items="4"
          categories="${this.getCategories()}"
          series="${JSON.stringify(this.getSeriesData())}"
          class="total-returns-chart"
        >
        </beacon-bar-chart>
        ${this.__renderHiddenDisclaimerCodes()}`,
    );
  }
}
